import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { CDN_URL } from '../../constants';
import { ArrowDownCircle, ArrowLeftCircle, ArrowRightCircle, CloseCircle } from '../Icons';
import QA from '../QA';
import AuthorHero from '../AuthorHero';
import Divider from '../Divider';
import Grid from '../Grid';
import Scholars from './../../components/Scholars';

const ArticleSlide = ({
  author,
  books,
  category,
  description,
  handleBack,
  handleNextArticle,
  handleNextSection,
  handleQASection,
  id,
  image,
  index,
  isVisible,
  isLastSlide,
  news,
  nextArticle,
  nextSlide,
  qa,
  scholars,
  tweets,
  title,
  subtitle,
}) => {
  if (!isVisible) {
    return null;
  }

  const hasQA = !!qa?.length;
  const hasNews = !!news?.length;
  const hasBooks = !!books?.length;
  const hasTweets = !!tweets?.length;
  const hasScholars = !!scholars?.length;
  const hasBodySecondary = hasNews || hasBooks || hasTweets;
  const qaSectionId = `${id}-qa`;

  if (scholars?.items?.length) {
    return <Scholars scholars={scholars?.items} title={scholars?.title} handleBack={handleBack} />;
  }

  return (
    <div
      className={classnames('article-slide', 'article-slide--open', {
        'article-slide--last': isLastSlide,
      })}
      id={id}
    >
      <div className='article-slide__header'>
        <div className='container'>
          <div className='row'>
            <div className='col position-relative'>
              <div className='article-slide__header-container'>
                {image && (
                  <div
                    className='article-slide__image'
                    style={{ backgroundImage: `url(${CDN_URL}${image})` }}
                  />
                )}
                <button className='article-slide__back-cta' onClick={() => handleBack()}>
                  <ArrowLeftCircle className='article-slide__back-cta-icon' />
                  <span className='article-slide__back-cta-title'>Back to all articles</span>
                </button>
                {subtitle && (
                  <div
                    className='article-slide__subtitle'
                    dangerouslySetInnerHTML={{ __html: subtitle }}
                  />
                )}
                {(title || category) && (
                  <div className='article-slide__header-content'>
                    {title && (
                      <div
                        className='article-slide__title'
                        dangerouslySetInnerHTML={{ __html: title }}
                      />
                    )}
                    {category && <div className='article-slide__category'>{category}</div>}
                  </div>
                )}
              </div>
              {!isLastSlide && (
                <button className='article-slide__cta' onClick={() => handleNextArticle(index)}>
                  <span className='article-slide__cta-title'>Next Article</span>
                  <ArrowRightCircle className='article-slide__cta-icon' />
                </button>
              )}
              {isLastSlide && (
                <button
                  className='article-slide__cta article-slide__cta--dark'
                  onClick={() => handleNextSection(index)}
                >
                  <span className='article-slide__cta-title'>Next Section</span>
                  <ArrowRightCircle className='article-slide__cta-icon' />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className='article-slide__body'>
        <div className='container'>
          <div className='row'>
            <div className='col'>
              <div className='article-slide__body-container'>
                <div className='article-slide__body-main'>
                  {hasQA && (
                    <button
                      className='article-slide__qa-cta'
                      onClick={() => handleQASection(qaSectionId)}
                    >
                      <ArrowDownCircle className='article-slide__qa-cta-icon' />
                      <span className='article-slide__qa-cta-title'>
                        Jump to Q&A with {author?.first_name} {author?.last_name}
                      </span>
                    </button>
                  )}
                  {description && (
                    <div
                      className='article-slide__description'
                      dangerouslySetInnerHTML={{ __html: description }}
                    />
                  )}
                  <AuthorHero {...author} id={qaSectionId} />
                  {hasQA && <QA items={qa} />}
                  {hasScholars &&
                    scholars.map((item, index) => (
                      <Grid {...item} className='scholars' key={index} />
                    ))}
                  <Divider />
                </div>
                {hasBodySecondary && (
                  <div className='article-slide__body-secondary'>
                    {hasNews && <Grid title='Related News' items={news} />}
                    {hasBooks && <Grid title='Related Books' items={books} cols={2} lgCols={4} />}
                    {hasTweets && <Grid title='Related Tweets' items={tweets} />}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='article-slide__footer'>
        <div className='container'>
          <div className='row'>
            <div className='col'>
              <div className='article-slide__footer-container'>
                <div className='article-slide__controls'>
                  <div className='article-slide__control article-slide__control--close'>
                    <button className='article-slide__control-cta' onClick={() => handleBack()}>
                      <CloseCircle className='article-slide__control-icon' />
                      <span className='article-slide__control-title'>Close</span>
                    </button>
                  </div>
                  {!isLastSlide && (
                    <div className='article-slide__control article-slide__control--next'>
                      <button
                        className='article-slide__control-cta'
                        onClick={() => handleNextArticle(index)}
                      >
                        <ArrowRightCircle className='article-slide__control-icon' />
                        <span className='article-slide__control-subtitle'>Next Article</span>
                        <span
                          className='article-slide__control-title'
                          dangerouslySetInnerHTML={{ __html: nextSlide?.title }}
                        />
                      </button>
                    </div>
                  )}
                  {isLastSlide && (
                    <div className='article-slide__control article-slide__control--next'>
                      <button
                        className='article-slide__control-cta'
                        onClick={() => handleNextSection(index)}
                      >
                        <ArrowRightCircle className='article-slide__control-icon' />
                        <span className='article-slide__control-subtitle'>Next Section</span>
                        <span
                          className='article-slide__control-title'
                          dangerouslySetInnerHTML={{ __html: nextArticle?.title }}
                        />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ArticleSlide.propTypes = {
  author: PropTypes.object,
  books: PropTypes.array,
  category: PropTypes.string,
  description: PropTypes.string,
  handleBack: PropTypes.func,
  handleNextArticle: PropTypes.func,
  handleNextSection: PropTypes.func,
  handleQASection: PropTypes.func,
  id: PropTypes.string,
  image: PropTypes.string,
  index: PropTypes.number,
  isVisible: PropTypes.bool,
  isLastSlide: PropTypes.bool,
  news: PropTypes.array,
  nextArticle: PropTypes.object,
  nextSlide: PropTypes.object,
  qa: PropTypes.array,
  scholars: PropTypes.any,
  tweets: PropTypes.array,
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

export default ArticleSlide;
